/**
 * Text helpers for the section editor: entity decoding (to show a slot's
 * value as plain text), escaping (to write a plain-text value back into
 * the HTML source), merge-token checks and URL checks.
 *
 * Everything a marketer types is plain text. It is written back fully
 * escaped; the only things let through unescaped are merge tokens that
 * the import path already accepts.
 */
import { importHtmlTemplate } from '../templates/html-import.js';
import { MERGE_FIELD_PATTERN, isAllowedMergeField } from '../templates/merge-fields.js';

const LATIN1 =
  'nbsp iexcl cent pound curren yen brvbar sect uml copy ordf laquo not shy reg macr deg plusmn sup2 sup3 acute micro para middot cedil sup1 ordm raquo frac14 frac12 frac34 iquest Agrave Aacute Acirc Atilde Auml Aring AElig Ccedil Egrave Eacute Ecirc Euml Igrave Iacute Icirc Iuml ETH Ntilde Ograve Oacute Ocirc Otilde Ouml times Oslash Ugrave Uacute Ucirc Uuml Yacute THORN szlig agrave aacute acirc atilde auml aring aelig ccedil egrave eacute ecirc euml igrave iacute icirc iuml eth ntilde ograve oacute ocirc otilde ouml divide oslash ugrave uacute ucirc uuml yacute thorn yuml';

const OTHER =
  'amp:38 lt:60 gt:62 quot:34 apos:39 OElig:338 oelig:339 Scaron:352 scaron:353 Yuml:376 ensp:8194 emsp:8195 thinsp:8201 zwnj:8204 zwj:8205 ndash:8211 mdash:8212 lsquo:8216 rsquo:8217 sbquo:8218 ldquo:8220 rdquo:8221 bdquo:8222 dagger:8224 Dagger:8225 bull:8226 hellip:8230 permil:8240 prime:8242 Prime:8243 lsaquo:8249 rsaquo:8250 euro:8364 trade:8482 larr:8592 uarr:8593 rarr:8594 darr:8595 harr:8596 rArr:8658 minus:8722 infin:8734 ne:8800 le:8804 ge:8805 star:9734 starf:9733 hearts:9829 check:10003';

/**
 * Named character references this module decodes: Latin-1 plus the
 * typographic ones seen in mail. A name outside this table is left as
 * written (`&nabla;`) and passes through unchanged when the text is
 * written back, so it still shows as the same character.
 */
const NAMED_ENTITIES: ReadonlyMap<string, number> = new Map<string, number>([
  ...LATIN1.split(' ').map((name, i): [string, number] => [name, 160 + i]),
  ...OTHER.split(' ').map((pair): [string, number] => {
    const [name, code] = pair.split(':');
    return [name!, Number(code)];
  }),
]);

const ENTITY_PATTERN = /&(#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[a-zA-Z][a-zA-Z0-9]{1,31});/g;

/** Decodes character references. Unknown names and invalid code points are left as written. */
export function decodeEntities(raw: string): string {
  if (!raw.includes('&')) return raw;
  return raw.replace(ENTITY_PATTERN, (whole, body: string) => {
    let code: number | undefined;
    if (body[0] === '#') {
      code =
        body[1] === 'x' || body[1] === 'X'
          ? Number.parseInt(body.slice(2), 16)
          : Number.parseInt(body.slice(1), 10);
    } else {
      code = NAMED_ENTITIES.get(body);
    }
    if (code === undefined || code === 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) {
      return whole;
    }
    return String.fromCodePoint(code);
  });
}

function escapeChar(ch: string): string {
  switch (ch) {
    case '&':
      return '&amp;';
    case '<':
      return '&lt;';
    case '>':
      return '&gt;';
    case '"':
      return '&quot;';
    case "'":
      return '&#39;';
    case ' ':
      return '&nbsp;';
    default:
      return `&#${ch.codePointAt(0)!};`;
  }
}

// Markup characters plus everything outside printable ASCII, so the
// result does not depend on the charset the mail declares.
const ESCAPE_PATTERN = /[&<>"']|[^\n\x20-\x7e]/gu;
const UNKNOWN_ENTITY_AT_START = /^&([a-zA-Z][a-zA-Z0-9]{1,31});/;

/** Escapes plain text for a text node. An unknown named reference (`&nabla;`) is kept, since {@link decodeEntities} left it as written. */
export function escapeText(text: string): string {
  return text.replace(ESCAPE_PATTERN, (ch, offset: number) => {
    if (ch === '&') {
      const match = UNKNOWN_ENTITY_AT_START.exec(text.slice(offset, offset + 34));
      if (match && !NAMED_ENTITIES.has(match[1]!)) return '&';
    }
    return escapeChar(ch);
  });
}

/** Escapes plain text for an attribute value (always written in double quotes or into existing quotes). */
export function escapeAttr(text: string): string {
  return text.replace(ESCAPE_PATTERN, escapeChar);
}

/** Collapses source whitespace the way a browser shows it; non-breaking spaces are kept. */
export function collapseWhitespace(text: string): string {
  return text.replace(/[ \t\r\n\f]+/g, ' ').replace(/^ | $/g, '');
}

/** True when the decoded text shows something (not only spaces, non-breaking spaces or zero-width characters). */
export function hasVisibleText(decoded: string): boolean {
  for (const char of decoded) {
    const code = char.codePointAt(0)!;
    // Zero-width space, non-joiner and joiner are not covered by \s.
    if (code >= 0x200b && code <= 0x200d) continue;
    if (!/\s/.test(char)) return true;
  }
  return false;
}

/** 53-bit string hash (cyrb53), base 36. Used only for stable ids, never for security. */
export function hashString(input: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i += 1) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

// --- merge tokens ---------------------------------------------------------

const MAILCHIMP_TOKEN = /\*\|[^|*{}<>"\\\r\n]{1,80}?\|\*/g;

function mergeFieldPattern(): RegExp {
  return new RegExp(MERGE_FIELD_PATTERN.source, 'g');
}

/** One combined pattern for both token syntaxes, in source order. */
function tokenPattern(): RegExp {
  return new RegExp(`${MERGE_FIELD_PATTERN.source}|${MAILCHIMP_TOKEN.source}`, 'g');
}

export interface MergeTokenInfo {
  /** Tokens as written, in order: `{{ contact.first_name }}`, `*|UNSUB|*`. */
  tokens: string[];
  /** Tokens the import path would refuse (unknown field, unsupported Mailchimp tag), with its message. */
  unknown: { token: string; message: string }[];
  /** A `{{`, `}}`, `*|` or `|*` is left over that is not part of a complete token. */
  malformed: boolean;
  unsubscribe: boolean;
  /** `{{ company_address }}`: the sender details from the brand kit. */
  sender: boolean;
}

/** Finds merge tokens in a decoded value and says which ones the existing import path would refuse. */
export function inspectMergeTokens(value: string): MergeTokenInfo {
  const info: MergeTokenInfo = {
    tokens: [],
    unknown: [],
    malformed: false,
    unsubscribe: false,
    sender: false,
  };
  if (!/\{\{|\}\}|\*\||\|\*/.test(value)) return info;
  const rest = value.replace(tokenPattern(), '');
  info.malformed = /\{\{|\}\}|\*\||\|\*/.test(rest);

  const imported = importHtmlTemplate(value);
  for (const error of imported.errors) {
    info.unknown.push({ token: error.tag, message: error.message });
  }
  for (const match of value.matchAll(tokenPattern())) info.tokens.push(match[0]);
  for (const match of imported.html.matchAll(mergeFieldPattern())) {
    const field = match[1]!;
    if (field === 'unsubscribe_url') info.unsubscribe = true;
    else if (field === 'company_address') info.sender = true;
    else if (!isAllowedMergeField(field)) {
      info.unknown.push({ token: match[0], message: `Disallowed merge field: {{ ${field} }}.` });
    }
  }
  return info;
}

export type ValueRefusal =
  'unknown_merge_field' | 'malformed_merge_field' | 'invalid_text' | 'unsafe_url';

export type Checked =
  { ok: true; value: string } | { ok: false; code: ValueRefusal; message: string };

function refuse(code: ValueRefusal, message: string): Checked {
  return { ok: false, code, message };
}

/**
 * Checks the merge tokens in a value a marketer typed. Refuses a token
 * the server check would refuse on save, half-written `{{ ... }}`, and a
 * token carrying markup characters. `allowDefault: false` (attributes)
 * also refuses `| default: "..."`, whose quotes cannot survive attribute
 * escaping.
 */
function checkMergeTokens(value: string, allowDefault: boolean): Checked {
  const info = inspectMergeTokens(value);
  if (info.unknown.length > 0) {
    return refuse('unknown_merge_field', info.unknown[0]!.message);
  }
  if (info.malformed) {
    return refuse(
      'malformed_merge_field',
      'A merge field is not written completely. Use the form {{ contact.first_name }}.',
    );
  }
  for (const token of info.tokens) {
    if (/[<>&]/.test(token)) {
      return refuse('malformed_merge_field', 'A merge field must not contain <, > or &.');
    }
    if (!allowDefault && token.includes('"')) {
      return refuse(
        'malformed_merge_field',
        'A merge field with a default text cannot be used in a link or an image description.',
      );
    }
  }
  return { ok: true, value };
}

// Control characters other than the line break the editor uses for <br>.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0009\u000b-\u001f\u007f]/;

export const MAX_TEXT_LENGTH = 5000;
export const MAX_ATTR_LENGTH = 2000;

/** Normalises and checks the text of a text slot. Line breaks (`\n`) become `<br>` when rendered. */
export function checkTextValue(input: string): Checked {
  const value = input
    .replace(/\r\n?/g, '\n')
    .replace(/\t/g, ' ')
    .split('\n')
    .map((line) => line.replace(/ {2,}/g, ' ').replace(/^ | $/g, ''))
    .join('\n')
    .replace(/^\n+|\n+$/g, '');
  if (value.length > MAX_TEXT_LENGTH) {
    return refuse('invalid_text', `The text is longer than ${MAX_TEXT_LENGTH} characters.`);
  }
  if (CONTROL_CHARS.test(value)) {
    return refuse('invalid_text', 'The text contains control characters.');
  }
  return checkMergeTokens(value, true);
}

/** Normalises and checks an image description (alt). One line of plain text. */
export function checkAltValue(input: string): Checked {
  const value = collapseWhitespace(input);
  if (value.length > MAX_ATTR_LENGTH) {
    return refuse('invalid_text', `The description is longer than ${MAX_ATTR_LENGTH} characters.`);
  }
  if (CONTROL_CHARS.test(value)) {
    return refuse('invalid_text', 'The description contains control characters.');
  }
  return checkMergeTokens(value, false);
}

/** Whitespace, control characters, quotes, angle brackets or a backslash. */
function hasForbiddenUrlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return /[\s"<>\\]/.test(value);
}

function checkUrlShape(input: string, what: string): Checked {
  const value = input.trim();
  if (value === '') return refuse('unsafe_url', `The ${what} is empty.`);
  if (value.length > MAX_ATTR_LENGTH) {
    return refuse('unsafe_url', `The ${what} is longer than ${MAX_ATTR_LENGTH} characters.`);
  }
  // Tokens may contain spaces ("{{ view_url }}"); nothing else may.
  if (hasForbiddenUrlCharacter(value.replace(tokenPattern(), ''))) {
    return refuse('unsafe_url', `The ${what} must not contain spaces, quotes or < >.`);
  }
  return { ok: true, value };
}

/**
 * A link target: an absolute http(s) address, `mailto:`, `tel:`, or a
 * single link merge field (`{{ view_url }}`, `*|ARCHIVE|*`). Everything
 * else is refused, which covers `javascript:`, `data:`, `vbscript:` and
 * relative addresses without having to list them.
 */
export function checkHrefValue(input: string): Checked {
  const shape = checkUrlShape(input, 'link');
  if (!shape.ok) return shape;
  const value = shape.value;
  const merge = checkMergeTokens(value, false);
  if (!merge.ok) return merge;
  const tokens = [...value.matchAll(tokenPattern())];
  const wholeIsToken = tokens.length === 1 && tokens[0]![0] === value;
  if (!wholeIsToken && !/^(?:https?:\/\/[^/?#]|mailto:.|tel:.)/i.test(value)) {
    return refuse(
      'unsafe_url',
      'A link must start with https://, http://, mailto: or tel:, or be a link merge field.',
    );
  }
  return { ok: true, value };
}

/** An image address: absolute http(s) only. No merge fields, no `data:`, no relative path. */
export function checkSrcValue(input: string): Checked {
  const shape = checkUrlShape(input, 'image address');
  if (!shape.ok) return shape;
  if (!/^https?:\/\/[^/?#]/i.test(shape.value) || /\{\{|\}\}|\*\||\|\*/.test(shape.value)) {
    return refuse('unsafe_url', 'An image address must start with https:// or http://.');
  }
  return { ok: true, value: shape.value };
}

/** Writes a checked text value: escaped text, merge tokens verbatim, `\n` as `br`. */
export function renderTextValue(value: string, br: string): string {
  const out: string[] = [];
  let last = 0;
  for (const match of value.matchAll(tokenPattern())) {
    out.push(escapeText(value.slice(last, match.index)), match[0]);
    last = match.index + match[0].length;
  }
  out.push(escapeText(value.slice(last)));
  return out.join('').replace(/\n/g, br);
}
